import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Download, Search, CheckCircle2, Square, Info, Save, Trash2, Star, Users as UsersIcon } from 'lucide-react';
import { downloadExport } from '../../utils/exportDownload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Presets courants pour éviter de recommencer à zéro
const PRESETS = [
  { id: 'quick_ops', label: 'Rapide — Ops + client', keys: ['operationNumber', 'phase', 'clientName', 'departure', 'arrival', 'deliveryDate', 'status'] },
  { id: 'quick_fin', label: 'Rapide — Finance', keys: ['operationNumber', 'clientName', 'estimatedPrice', 'invoiceAmount', 'margin', 'financialStatus', 'invoiceNumber', 'dueDate'] },
  { id: 'quick_margin', label: 'Marge détaillée', keys: ['operationNumber', 'clientName', 'estimatedPrice', 'carrierPrice', 'margin', 'marginPercent', 'transporteur'] },
];

export default function CustomExportModal({ open, onClose, onToast }) {
  const [catalog, setCatalog] = useState({ categories: [], columns: [] });
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState('');
  const [exporting, setExporting] = useState(false);
  // ✨ Préréglages personnalisés (23/04/2026)
  const [myPresets, setMyPresets] = useState([]);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveShared, setSaveShared] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchPresets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/export-presets/operations`);
      setMyPresets(res.data.presets || []);
    } catch (e) {
      // silent fail — la modale doit rester fonctionnelle même si presets indispos
    }
  }, []);

  // Charger le catalogue + presets au premier open
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/export/operations/catalog`);
        setCatalog(res.data);
        await fetchPresets();
      } catch (e) {
        if (onToast) onToast({ title: 'Erreur', description: 'Catalogue indisponible', variant: 'destructive' });
      }
    })();
  }, [open, onToast, fetchPresets]);

  const groupedColumns = useMemo(() => {
    const groups = {};
    catalog.columns.forEach(col => {
      if (!groups[col.category]) groups[col.category] = [];
      if (!query || col.label.toLowerCase().includes(query.toLowerCase())) {
        groups[col.category].push(col);
      }
    });
    return groups;
  }, [catalog.columns, query]);

  const toggle = useCallback((key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const applyPreset = useCallback((keys) => {
    setSelected(new Set(keys));
  }, []);

  const selectAll = useCallback(() => {
    setSelected(new Set(catalog.columns.map(c => c.key)));
  }, [catalog.columns]);

  const clearAll = useCallback(() => setSelected(new Set()), []);

  // ✨ Sauvegarder la sélection actuelle
  const handleSavePreset = async () => {
    const name = saveName.trim();
    if (name.length < 2) {
      if (onToast) onToast({ title: 'Nom trop court', variant: 'destructive' });
      return;
    }
    if (selected.size === 0) {
      if (onToast) onToast({ title: 'Sélectionnez au moins 1 colonne', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const orderedKeys = catalog.columns.filter(c => selected.has(c.key)).map(c => c.key);
      await axios.post(`${API}/export-presets/operations`, {
        name,
        columns: orderedKeys,
        shared: saveShared,
      });
      if (onToast) onToast({ title: 'Préréglage enregistré', description: `« ${name} »` });
      setShowSaveForm(false);
      setSaveName('');
      setSaveShared(false);
      await fetchPresets();
    } catch (e) {
      if (onToast) onToast({
        title: 'Erreur',
        description: e.response?.data?.detail || 'Échec',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePreset = async (presetId, name) => {
    if (!window.confirm(`Supprimer le préréglage « ${name} » ?`)) return;
    try {
      await axios.delete(`${API}/export-presets/${presetId}`);
      if (onToast) onToast({ title: 'Préréglage supprimé' });
      await fetchPresets();
    } catch (e) {
      if (onToast) onToast({
        title: 'Erreur',
        description: e.response?.data?.detail || 'Échec',
        variant: 'destructive',
      });
    }
  };

  const handleExport = async (format = 'xlsx') => {
    if (selected.size === 0) {
      if (onToast) onToast({ title: 'Aucune colonne', description: 'Sélectionnez au moins 1 colonne', variant: 'destructive' });
      return;
    }
    setExporting(true);
    try {
      const orderedKeys = catalog.columns.filter(c => selected.has(c.key)).map(c => c.key);
      await downloadExport(`${API}/export/operations/custom?columns=${orderedKeys.join(',')}`, {
        format,
        filenameBase: 'BaobabConnect_Operations_Personnalise',
        axiosConfig: { headers: { Authorization: 'Bearer cookie-auth' } },
      });
      if (onToast) onToast({ title: 'Export réussi', description: `${selected.size} colonnes exportées` });
      onClose();
    } catch (e) {
      if (onToast) onToast({ title: 'Erreur export', description: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const categoryLabel = (key) => catalog.categories.find(c => c.key === key)?.label || key;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="custom-export-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-[#1db584]" />
            Export personnalisé
          </DialogTitle>
          <DialogDescription>
            Choisissez les colonnes puis le format (Excel structuré ou CSV)
          </DialogDescription>
        </DialogHeader>

        {/* ✨ Mes préréglages sauvegardés */}
        {myPresets.length > 0 && (
          <div className="pb-2 border-b">
            <div className="text-xs text-gray-500 mb-1.5 flex items-center gap-1">
              <Star className="w-3 h-3" /> Mes préréglages
            </div>
            <div className="flex flex-wrap gap-1.5">
              {myPresets.map(p => (
                <div
                  key={p.id}
                  className="group inline-flex items-center gap-1 pl-2 pr-0.5 h-7 rounded-full border border-amber-200 bg-amber-50 hover:bg-amber-100 text-xs transition"
                  data-testid={`saved-preset-${p.id}`}
                >
                  <button
                    type="button"
                    onClick={() => applyPreset(p.columns)}
                    className="inline-flex items-center gap-1"
                  >
                    <span className="font-medium text-amber-800">{p.name}</span>
                    <span className="text-[10px] text-amber-600">({p.columns.length})</span>
                    {p.shared && (
                      <UsersIcon className="w-3 h-3 text-blue-500" aria-label="Partagé avec l'équipe" />
                    )}
                    {!p.isOwner && p.userName && (
                      <span className="text-[10px] text-gray-500">· {p.userName}</span>
                    )}
                  </button>
                  {p.isOwner !== false && (
                    <button
                      type="button"
                      onClick={() => handleDeletePreset(p.id, p.name)}
                      className="w-5 h-5 rounded-full hover:bg-red-100 text-amber-600 hover:text-red-600 flex items-center justify-center"
                      data-testid={`delete-preset-${p.id}`}
                      title="Supprimer"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Presets rapides prédéfinis */}
        <div className="flex flex-wrap items-center gap-2 pb-2 border-b">
          <span className="text-xs text-gray-500 mr-1">Préréglages rapides :</span>
          {PRESETS.map(p => (
            <Button
              key={p.id}
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => applyPreset(p.keys)}
              data-testid={`preset-${p.id}`}
            >
              {p.label}
            </Button>
          ))}
        </div>

        <div className="flex items-center gap-2 py-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Rechercher une colonne..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-8 h-9"
              data-testid="search-columns-input"
            />
          </div>
          <Button size="sm" variant="ghost" onClick={selectAll} data-testid="select-all-btn">
            Tout sélectionner
          </Button>
          <Button size="sm" variant="ghost" onClick={clearAll} data-testid="clear-all-btn">
            Tout vider
          </Button>
        </div>

        {/* Liste groupée */}
        <div className="flex-1 overflow-y-auto border rounded-lg p-1 min-h-[240px]">
          {Object.keys(groupedColumns).length === 0 && (
            <p className="text-center text-sm text-gray-400 py-8">Aucune colonne ne correspond</p>
          )}
          {Object.entries(groupedColumns).map(([catKey, cols]) => (
            cols.length === 0 ? null : (
              <div key={catKey} className="mb-3">
                <div className="sticky top-0 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-600 border-b">
                  {categoryLabel(catKey)} <span className="text-gray-400">({cols.length})</span>
                </div>
                <ul className="divide-y">
                  {cols.map(col => {
                    const checked = selected.has(col.key);
                    return (
                      <li
                        key={col.key}
                        onClick={() => toggle(col.key)}
                        className={`flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-gray-50 transition ${checked ? 'bg-green-50/50' : ''}`}
                        data-testid={`column-${col.key}`}
                      >
                        <div className="mt-0.5">
                          {checked
                            ? <CheckCircle2 className="w-4 h-4 text-[#1db584]" />
                            : <Square className="w-4 h-4 text-gray-300" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-900">{col.label}</div>
                          {col.description && (
                            <div className="text-[11px] text-gray-500 truncate">{col.description}</div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )
          ))}
        </div>

        {/* ✨ Formulaire de sauvegarde */}
        {showSaveForm ? (
          <div className="border-t pt-2 space-y-2">
            <div className="text-xs text-gray-600 font-medium flex items-center gap-1">
              <Save className="w-3 h-3" /> Enregistrer comme préréglage
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Ex: Export compta fin de mois"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                className="flex-1 h-9"
                data-testid="preset-name-input"
                autoFocus
              />
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveShared}
                  onChange={(e) => setSaveShared(e.target.checked)}
                  className="w-3.5 h-3.5 accent-[#1db584]"
                  data-testid="preset-shared-checkbox"
                />
                Partager avec l'équipe
              </label>
              <Button
                size="sm"
                onClick={handleSavePreset}
                disabled={saving || saveName.trim().length < 2 || selected.size === 0}
                className="bg-[#1db584] hover:bg-[#18a075] text-white"
                data-testid="save-preset-confirm"
              >
                {saving ? '...' : 'Enregistrer'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowSaveForm(false); setSaveName(''); }}>
                Annuler
              </Button>
            </div>
          </div>
        ) : null}

        <div className="flex items-center justify-between py-2 border-t">
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <Info className="w-3 h-3" />
            <Badge variant="outline" className="mr-1">{selected.size}</Badge>
            colonne{selected.size > 1 ? 's' : ''} sélectionnée{selected.size > 1 ? 's' : ''}
          </div>
          {!showSaveForm && selected.size > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowSaveForm(true)}
              className="text-xs h-7"
              data-testid="save-preset-btn"
            >
              <Save className="w-3 h-3 mr-1" />
              Enregistrer sous...
            </Button>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={exporting}>Annuler</Button>
          <Button
            variant="outline"
            onClick={() => handleExport('csv')}
            disabled={exporting || selected.size === 0}
            data-testid="confirm-custom-export-csv"
          >
            <Download className="w-4 h-4 mr-1" />
            CSV
          </Button>
          <Button
            onClick={() => handleExport('xlsx')}
            disabled={exporting || selected.size === 0}
            className="bg-[#1db584] hover:bg-[#18a075] text-white"
            data-testid="confirm-custom-export"
          >
            <Download className="w-4 h-4 mr-1" />
            {exporting ? 'Export en cours...' : `Excel (${selected.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
